import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import errorIcon from "../img/error-toast-icon.svg";
import successIcon from "../img/toast-success-icon.svg";
import warningIcon from "../img/toast-warning-icon.svg";
import infoIcon from "../img/toast-info-icon.svg";

let toastLabel: string | undefined;
let type: string | undefined;
let typeIcon: any | undefined;
let deleteAuto = true;
let expire = "5000";
let positioning = "top-right";

export interface ToastProps {
  autoDelete?: boolean | undefined;
  autoDeleteTime?: any | undefined;
  position?: string | undefined;
  toastList?: any | undefined;
}

export interface toastCall {
  info: (text: string, options?: any) => void;
  error: (text: string, options?: any) => void;
  success: (text: string, options?: any) => void;
  warning: (text: string, options?: any) => void;
}

const close = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512">
    <path d="M310.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L160 210.7 54.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L114.7 256 9.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 301.3 265.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L205.3 256 310.6 150.6z" />
  </svg>
);

const Toast: React.FC<any> = (props) => {
  const { toastList, autoDelete, autoDeleteTime } = props;
  const [list, setList] = useState(toastList);
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseEnter = () => {
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  useEffect(() => {
    setList([...toastList]);
  }, [toastList]);

  useEffect(() => {
    if (isHovering)
      setTimeout(() => {
        setIsHovering(false);
      }, 2000);
  }, [isHovering]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (autoDelete && toastList?.length && list?.length && !isHovering) {
        deleteToast(toastList[0]?.id);
      }
    }, autoDeleteTime);

    return () => {
      clearInterval(interval);
    };
    //eslint-disable-next-line
  }, [autoDelete, autoDeleteTime, isHovering, list, toastList]);

  const deleteToast = (id: number) => {
    const listItemIndex = list?.findIndex((e: any) => e?.id === id);
    const toastListItem = toastList?.findIndex((e: any) => e.id === id);
    list.splice(listItemIndex, 1);
    toastList.splice(toastListItem, 1);
    setList([...list]);
  };

  return createPortal(
    <div className={`notification-container top-right`}>
      {list.map((toast: any, i: any) => {
        return (
          <div
            key={i}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={`notification  ${toast.position}`}
          >
            <div
              className={`toast-wrap ${
                toast.type === "error"
                  ? "toast-wrap__error"
                  : toast.type === "success"
                  ? "toast-wrap__success"
                  : toast.type === "info"
                  ? "toast-wrap__info"
                  : "toast-wrap__warning"
              }`}
            >
              <figure className="img-box">
                <img src={toast.icon} alt="" className="img" />
              </figure>
              <p className="text">{toast.title}</p>
              <div onClick={() => deleteToast(toast.id)} className="cancel">
                {close}
              </div>
              <div
                style={{
                  animationPlayState: isHovering ? "paused" : "running",
                  animationName: "raven-toast-progress-animation",
                  animationDuration: `${autoDeleteTime}ms`,
                  animationTimingFunction: "linear",
                  animationFillMode: "forwards",
                  backgroundColor:
                    toast.type === "error"
                      ? "#FF0F00"
                      : toast.type === "success"
                      ? "#0B8376"
                      : toast.type === "info"
                      ? "#476885"
                      : "#EA872D",
                }}
                className="raven-toast-progress"
              ></div>
            </div>
          </div>
        );
      })}
    </div>,
    document.body // Render directly to the body
  );
};

const RavenToast: React.FC<ToastProps> = () => {
  const [list, setList] = useState<any>([]);

  const show = () => {
    const id = Math.floor(Math.random() * 101 + 1);
    //fire function only if toast doesn't exist yet
    if (!list.some((obj: any) => obj.title === toastLabel)) {
      setList([
        ...list,
        {
          id,
          title: toastLabel,
          icon: typeIcon,
          type: type,
          position: positioning,
        },
      ]);
    }
  };

  return (
    <>
      <button className={"raven-toast-show"} onClick={show}></button>
      <Toast autoDelete={deleteAuto} autoDeleteTime={expire} toastList={list} />
    </>
  );
};

const toast: toastCall = {
  error: (text, options) => {
    const toastElement = document.querySelector(
      ".raven-toast-show"
    ) as HTMLElement;
    const {
      autoDelete = true,
      expireIn = "5000",
      position = "top-right",
    } = options || {};

    toastLabel = text;
    type = "error";
    typeIcon = errorIcon;
    deleteAuto = autoDelete ? autoDelete : true;
    expire = expireIn ? expireIn : "5000";
    positioning = position ? position : "top-right";
    if (toastLabel === text) {
      toastElement?.click();
    }
  },

  success: (text, options) => {
    const toastElement = document.querySelector(
      ".raven-toast-show"
    ) as HTMLElement;
    const {
      autoDelete = true,
      expireIn = "5000",
      position = "top-right",
    } = options || {};

    toastLabel = text;
    type = "success";
    typeIcon = successIcon;
    deleteAuto = autoDelete ? autoDelete : true;
    expire = expireIn ? expireIn : "5000";
    positioning = position ? position : "top-right";
    if (toastLabel === text) {
      toastElement?.click();
    }
  },

  info: (text, options) => {
    const toastElement = document.querySelector(
      ".raven-toast-show"
    ) as HTMLElement;
    const {
      autoDelete = true,
      expireIn = "5000",
      position = "top-right",
    } = options || {};

    toastLabel = text;
    type = "info";
    typeIcon = infoIcon;
    deleteAuto = autoDelete ? autoDelete : true;
    expire = expireIn ? expireIn : "5000";
    positioning = position ? position : "top-right";
    if (toastLabel === text) {
      toastElement?.click();
    }
  },

  warning: (text, options) => {
    const toastElement = document.querySelector(
      ".raven-toast-show"
    ) as HTMLElement;
    const {
      autoDelete = true,
      expireIn = "5000",
      position = "top-right",
    } = options || {};
    toastLabel = text;
    type = "warning";
    typeIcon = warningIcon;
    deleteAuto = autoDelete ? autoDelete : true;
    expire = expireIn ? expireIn : "5000";
    positioning = position ? position : "top-right";
    if (toastLabel === text) {
      toastElement?.click();
    }
  },
};

export default RavenToast;
export { toast };
